import {UserInterfaceHtmlElements} from "../UserInterfaceHtmlElements/UserInterfaceHtmlElements";
import {getMatchingNamesWithBoldedSearchedPattern} from "./getMatchingNames";
import {PhpDataProvider} from "../PhpDataProvider/PhpDataProvider";
import {AccentService} from "../AccentService/AccentService";
import {runSearch} from "../runSearch/boldSearchPatterInString";

export class SearchWhispererHtmlElement extends HTMLElement
{
    template = `
			<style>
				#whisperer-container {
				    position: absolute;
				    min-width: 175px;
				    font-size: 12px;
                    color: black;
				    background-color: white;
				    border: 1px solid #c5c5c5;
				    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: block;
                    outline: 0;
                    z-index: 100;
				}
				
				#whisperer-container li {
				    height: 20px;
				    display: block;
				    cursor: pointer;
				    margin: 0;
				    padding: 3px 1em 3px 0.4em;
				    /* Neviditelny border aby nedochazelo k poskakovani pro pridani borderu u hover. */
				    border: thin solid white; 
                }
                
                .activeItem {
                    color: black;
                    background: #95acbe;
                    border: thin solid #4A6C84;
                }
			</style>
			
			<ul id="whisperer-container"></ul>
		`;

    /** Defining maximum number of suggestions in the whisperer element. */
    private static maxNumberOfSuggestions = 20;

    private shadow: ShadowRoot;

    private readonly phpData:       PhpDataProvider;
    private readonly userInterface: UserInterfaceHtmlElements;
    private readonly accentService: AccentService;

    private readonly container: HTMLUListElement;
    private activeItemIndex: number | null = null;

    private whisperingSuggestionsList: string[] | undefined;

    constructor() {
        super();

        // Inicializace shadow DOMu.
        this.shadow = this.attachShadow({ mode: "open" });
        this.shadow.innerHTML = this.template;

        // Ziskani cointaineru a schovani.
        this.container = this.shadow.querySelector('ul#whisperer-container') as HTMLUListElement;
        this.container.style.display = 'none';

        // Potrebne instance trid.
        this.phpData             = new PhpDataProvider();
        this.userInterface       = new UserInterfaceHtmlElements();
        this.accentService = new AccentService(this.phpData.accentMap);

        // Poklud se klikne na jednu z moznosti naseptavace (nejaky li element). Vlozime text teto moznosti do
        //      vyhledavaciho pole, naseptavac schovame a spustime vyhledavani.
        this.container.addEventListener('click', (event: MouseEvent) => {
            if (! (event.target instanceof HTMLLIElement)) {
                return;
            }

            this.userInterface.searchInputField.value = (event.target as HTMLLIElement).innerText;

            runSearch(this.userInterface, this, this.phpData.domain);
        });

        // Poklud se mysi najede na jednu z moznosti naseptavace (nejaky li element). Nastavime tento element jako
        //      aktivni (obarveni elementu atd).
        this.container.addEventListener('mouseover', (event: MouseEvent) => {
            if (! (event.target instanceof HTMLLIElement)) {
                return;
            }

            this.setActiveItem(event.target as HTMLLIElement);
        });

        // Pokud mysi vyjedeme z containeru, tak zrusime aktivni element.
        this.container.addEventListener('mouseleave', () => {
            this.resetActiveItem();
        });

        // Pokud klikneme mimo vyhledavaci box, tak naseptavac schovame.
        document.addEventListener('click', (event: MouseEvent) => {
            if (! this.userInterface.searchInputField.contains(event.target as HTMLElement)) {
                this.hideSuggestions();
            }
        });
    }

    // connectedCallback() {}
    // attributeChangedCallback(name: string, oldValue: string, newValue: string) {}

    public setPositionUnderSearchBar(): void {
        const rect = this.userInterface.searchInputField.getBoundingClientRect();

        this.container.style.top = `${rect.top + window.scrollY + 25}px`;
        this.container.style.left = `${rect.left}px`;
    }

    public hideSuggestions(): void {
        // Odebereme vsechny nabizene moznosti naseptavace, tedy vsechny child elementy(li elementy).
        this.container.querySelectorAll('*').forEach((element: Element) => { element.remove() });

        this.container.style.display = 'none';
    }

    public showSuggestions(searchedQuery: string): void {
        if (this.whisperingSuggestionsList === undefined) {
            throw new Error('Whisperer suggestions not loaded.');
        }

        const boldedSuggestionsList = getMatchingNamesWithBoldedSearchedPattern(
            this.accentService, searchedQuery, this.whisperingSuggestionsList
        );

        const limitedBoldedSuggestionsList = (boldedSuggestionsList.length > SearchWhispererHtmlElement.maxNumberOfSuggestions)
            ? boldedSuggestionsList.slice(0, SearchWhispererHtmlElement.maxNumberOfSuggestions)
            : boldedSuggestionsList;

        // Odebereme vsechny nabizene moznosti naseptavace, tedy vsechny child elementy(li elementy).
        this.container.querySelectorAll('*').forEach((element: Element) => { element.remove() });

        // Vytvorime moznosti naseptavace, tedy child elementy(li elementy). A pridame je do containeru(ul elementu).
        Object.entries(limitedBoldedSuggestionsList).forEach(([key, value]: [string, string]) => {
            const liElement = document.createElement('li');
            liElement.innerHTML = value;

            this.container.appendChild(liElement);
        });

        this.setPositionUnderSearchBar();
        this.resetActiveItem();
        this.container.style.display = 'block';
    }

    public setSuggestionsList(list: string[]): void {
        if (this.whisperingSuggestionsList !== undefined) {
            throw new Error('Repeatedly setting suggestions list.')
        }

        this.whisperingSuggestionsList = list;
    }

    public resetActiveItem(): void {
        if (this.activeItemIndex === null || this.container.children.item(this.activeItemIndex) === null) {
            return;
        }

        (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.remove('activeItem');
        this.activeItemIndex = null;
    }


    public setActiveItem(liElement: HTMLLIElement): void {
        if (this.activeItemIndex !== null) {
            this.resetActiveItem();
        }

        this.activeItemIndex = Array.from(this.container.children).indexOf(liElement);
        (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.add('activeItem');
    }


    public setNextItemAsActive(): void {
        // Odebrat class u soucasneho.
        if (this.activeItemIndex !== null) {
            (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.remove('activeItem');
        }

        // Nastavime dalsi prvek jako aktivni.
        this.activeItemIndex = (this.activeItemIndex !== null && this.activeItemIndex + 1 < this.container.children.length)
            ? this.activeItemIndex = this.activeItemIndex + 1
            : this.activeItemIndex = 0;

        // Pridat class u dalsiho nyni soucasneho.
        (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.add('activeItem');

        // Nastavit text search baru aby odpovidal textu aktivniho elementu.
        this.userInterface.searchInputField.value = (this.container.children.item(this.activeItemIndex) as HTMLLIElement).innerText;
    }

    public setPreviousItemAsActive(): void {
        // Odebrat class u soucasneho.
        if (this.activeItemIndex !== null) {
            (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.remove('activeItem');
        }

        // Nastavime predchozi prvek jako aktivni.
        this.activeItemIndex = (this.activeItemIndex !== null && this.activeItemIndex > 0)
            ? this.activeItemIndex = this.activeItemIndex - 1
            : this.activeItemIndex = this.container.children.length - 1;

        // Pridat class u dalsiho nyni soucasneho.
        (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.add('activeItem');

        // Nastavit text search baru aby odpovidal textu aktivniho elementu.
        this.userInterface.searchInputField.value = (this.container.children.item(this.activeItemIndex) as HTMLLIElement).innerText;
    }

}